import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Download, LayoutTemplate, ChevronDown } from 'lucide-react';
import { Button } from '@ui/button';
import { PaginationControls, usePagination } from '@/shared/components/pagination';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { BaseLayout } from '@/components/layout';
import type { PunchlistFilters } from '../types';
import { usePunchLists } from '../hooks/punch-list-hooks';
import PunchListsDashboard from './PunchListsDashboard';
import PunchListsFilters from './PunchListsFilters';
import PunchListsTable from './PunchListsTable';
import PunchListCreateDialog from './PunchListCreateDialog';

const DEFAULT_FILTERS: PunchlistFilters = {
  search: '',
  status: 'all',
  type: 'instance',
  sortBy: 'updated_at',
  sortOrder: 'desc',
};

const PunchListsPage = () => {
  const navigate = useNavigate();
  const [filters, setFilters] = useState<PunchlistFilters>(DEFAULT_FILTERS);
  const [totalItems, setTotalItems] = useState(0);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [paginationState, paginationActions, paginationInfo] = usePagination(totalItems, 25);
  const { data, isLoading, fetchStatus } = usePunchLists(
    { ...filters, type: 'instance' },
    paginationState.currentPage,
    paginationState.itemsPerPage
  );
  const isActuallyLoading = isLoading && fetchStatus === 'fetching';

  useEffect(() => {
    setTotalItems(data?.filteredCount ?? 0);
  }, [data?.filteredCount]);

  const onViewDetail = (punchlistId: string) => {
    navigate(`./${punchlistId}`);
  };

  return (
    <BaseLayout>
      <div className="p-4 md:p-6 space-y-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Punch Lists</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Manage punch list instances for this project
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm">
              <Download className="h-4 w-4 mr-2" />
              Export
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm">
                  <Plus className="h-4 w-4 mr-2" />
                  Create
                  <ChevronDown className="h-3 w-3 ml-1" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setIsCreateDialogOpen(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  New Punch List
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('./templates')}>
                  <LayoutTemplate className="h-4 w-4 mr-2" />
                  Manage Templates
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        <PunchListsDashboard />

        <PunchListsFilters
          filters={filters}
          onFiltersChange={(nextFilters) => {
            setFilters({ ...nextFilters, type: 'instance' });
            paginationActions.setCurrentPage(1);
          }}
          totalCount={data?.totalCount ?? 0}
          filteredCount={data?.filteredCount ?? 0}
          hideTypeFilter={true}
        />

        <PunchListsTable
          punchlists={data?.items ?? []}
          isLoading={isActuallyLoading}
          onViewDetail={onViewDetail}
        />
        {!isActuallyLoading && (data?.filteredCount ?? 0) > 0 && (
          <PaginationControls
            state={paginationState}
            actions={paginationActions}
            info={paginationInfo}
          />
        )}
      </div>
      <PunchListCreateDialog
        open={isCreateDialogOpen}
        onOpenChange={setIsCreateDialogOpen}
        onCreateFromScratch={(areaId) =>
          navigate(
            areaId === null
              ? './templates/new?type=instance'
              : `./templates/new?type=instance&area=${areaId}`
          )
        }
        onCreatedFromTemplate={(newInstanceId) => navigate(`./${newInstanceId}`)}
      />
    </BaseLayout>
  );
};

export default PunchListsPage;
